// src/components/plaid/usePlaidOffer.js
import { useEffect, useRef, useState } from "react";
import { usePlaidLink } from "react-plaid-link";
import {
  createPlaidLinkToken,
  exchangePlaidPublicToken,
} from "../../shared/_services/common.services";
import {
  getSettingsConsents,
  setConsent,
} from "../../shared/_services/onboarding.services";
import { readCurrentUser } from "../../shared/_helpers/currentUser";

// The web's one Plaid link flow (spec § OAuth return handling, owner items 11
// to 13), grown from SP4's usePlaidOffer: Q5/Q6's panel, the Questions page
// row and Profile's Linked accounts panel all link through it.
//
// Consent first: with the effective `plaid` consent off the caller shows the
// consent row (`consentRow`); ticking it writes POST /consents { kind:
// 'plaid', state: true } and then asks for a link token. A link token refused
// 409 `consent_required` (the consent was switched off elsewhere) shows the
// consent row too.
//
// OAuth banks: the link token and the page that asked for it are kept in
// sessionStorage before Link opens. When the bank sends the browser back to
// `redirectPath` with `oauth_state_id`, the hook on that page re-opens Link
// with the stored token and `receivedRedirectUri: window.location.href`;
// when Link finishes, the token and `oauth_state_id` are cleared. Redirect
// paths (registered per environment in the Plaid dashboard): R204's
// /intake/q5_debts, /intake/q6_investments, /questionnaire, and /profile.
//
// `onLinked()` runs after a successful exchange and answers a failure kind or
// null; `onStatus` hears { consent, linked }; `failed` is "plaid" (Plaid or
// the exchange failed) or "generic" (the consent read or write failed).
export const PLAID_LINK_KEY = "astra.plaidLink";
const OAUTH_PARAM = "oauth_state_id";

const stored = () => {
  try {
    return JSON.parse(sessionStorage.getItem(PLAID_LINK_KEY));
  } catch (_) {
    return null;
  }
};
const store = (value) => {
  try {
    if (value) sessionStorage.setItem(PLAID_LINK_KEY, JSON.stringify(value));
    else sessionStorage.removeItem(PLAID_LINK_KEY);
  } catch (_) {
    // Storage refused (private mode): only an OAuth bank's return is lost.
  }
};
// This page is Plaid's OAuth return for a link this page started.
const oauthReturn = (redirectPath) => {
  const here = new URL(window.location.href);
  const kept = stored();
  if (!here.searchParams.has(OAUTH_PARAM) || kept?.path !== redirectPath)
    return null;
  return kept.token ? { token: kept.token, uri: window.location.href } : null;
};
const dropOauthParam = () => {
  const url = new URL(window.location.href);
  if (!url.searchParams.has(OAUTH_PARAM)) return;
  url.searchParams.delete(OAUTH_PARAM);
  window.history.replaceState(
    window.history.state,
    "",
    `${url.pathname}${url.search}${url.hash}`,
  );
};

export function usePlaidOffer({ redirectPath, consent, onLinked, onStatus }) {
  const [resume] = useState(() => oauthReturn(redirectPath));
  const [consented, setConsented] = useState(consent);
  const [consentRow, setConsentRow] = useState(null);
  const [token, setToken] = useState(resume?.token ?? null);
  const [failed, setFailed] = useState(null);
  const giving = useRef(false);
  const linking = useRef(false);
  const opened = useRef(null);
  const alive = useRef(false);
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);
  // A caller that learns the consent after its first render (Profile reads
  // GET /plaid/links) passes it when known.
  useEffect(() => {
    setConsented(consent);
  }, [consent]);
  const userId = readCurrentUser()?.user?._id;

  const done = () => {
    store(null);
    if (resume) dropOauthParam();
  };
  const { open, ready } = usePlaidLink({
    token,
    ...(resume && token === resume.token
      ? { receivedRedirectUri: resume.uri }
      : {}),
    onSuccess: async (publicToken) => {
      done();
      try {
        await exchangePlaidPublicToken(publicToken, userId);
        if (!alive.current) return;
        onStatus?.({ consent: true, linked: true });
        const next = (await onLinked?.()) ?? null;
        if (alive.current) setFailed(next);
      } catch (_) {
        if (alive.current) setFailed("plaid");
      }
    },
    onExit: (err) => {
      done();
      if (err && alive.current) setFailed("plaid");
    },
  });
  // Opens Link once per token: after a fresh token, or on the OAuth return.
  useEffect(() => {
    if (!token || !ready || opened.current === token) return;
    opened.current = token;
    open();
  }, [token, ready, open]);

  const askConsent = async () => {
    try {
      const res = await getSettingsConsents();
      if (alive.current)
        setConsentRow(res.data.consents.find((c) => c.kind === "plaid"));
    } catch (_) {
      if (alive.current) setFailed("generic");
    }
  };
  const link = async () => {
    if (linking.current) return;
    linking.current = true;
    setFailed(null);
    try {
      const next = await createPlaidLinkToken(
        userId,
        `${process.env.REACT_APP_URL}${redirectPath}`,
      );
      if (!alive.current) return;
      store({ token: next, path: redirectPath });
      setToken(next);
    } catch (err) {
      if (!alive.current) return;
      if (err?.response?.data?.code === "consent_required") {
        setConsented(false);
        await askConsent();
      } else {
        setFailed("plaid");
      }
    } finally {
      linking.current = false;
    }
  };
  const start = async () => {
    setFailed(null);
    if (consented) return link();
    return askConsent();
  };
  const giveConsent = async () => {
    if (giving.current) return;
    giving.current = true;
    setFailed(null);
    try {
      await setConsent("plaid", true);
      if (!alive.current) return true;
      setConsented(true);
      onStatus?.({ consent: true });
      setConsentRow(null);
      await link();
      return true;
    } catch (_) {
      if (alive.current) setFailed("generic");
      return false;
    } finally {
      giving.current = false;
    }
  };
  return { start, consentRow, giveConsent, failed, setFailed };
}
